import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { ZapIcon, CircleIcon, BatteryChargingIcon, FootprintsIcon, CheckIcon } from 'lucide-react';
interface ServicesPageProps {
  onNavigate: (page: string) => void;
}
export function ServicesPage({
  onNavigate
}: ServicesPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [{
    id: 'scooters',
    title: 'Electric Scooters',
    description: 'We have a wide range of mobility scooters suitable for individuals of varying weights and heights. Our scooters are designed to be easily disassembled, making them highly convenient for transportation in most standard vehicles.',
    icon: ZapIcon,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    features: ['Lightweight & portable', 'Easy to disassemble', 'Long battery life', 'Adjustable seating'],
    image: 'https://wheelaroundrentals.com/wp-content/uploads/2022/10/Image-3-280x242.png'
  }, {
    id: 'manual-wheelchairs',
    title: 'Manual Wheelchairs',
    description: 'Comfortable and reliable manual wheelchairs for daily use, perfect for short trips and easy folding. Our manual wheelchairs are lightweight, foldable, and easy to transport, providing a simple and effective mobility solution.',
    icon: CircleIcon,
    color: 'text-brand-blue',
    bgColor: 'bg-blue-50',
    features: ['Lightweight frame', 'Easily foldable', 'Comfortable seating', 'Swing-away footrests'],
    image: 'https://wheelaroundrentals.com/wp-content/uploads/2022/10/Image-2-280x242.png'
  }, {
    id: 'power-wheelchairs',
    title: 'Power Wheelchairs',
    description: 'Electric-powered wheelchairs for enhanced mobility and independence. Ideal for extended use with long-lasting batteries and comfortable seating. These chairs offer tight turning radiuses, making them perfect for navigating indoor spaces and crowded tourist attractions.',
    icon: BatteryChargingIcon,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
    features: ['Joystick control', 'Tight turning radius', 'Extended battery range', 'Premium comfort seating'],
    image: 'https://wheelaroundrentals.com/wp-content/uploads/2022/10/Image-280x242.png'
  }, {
    id: 'knee-walkers',
    title: 'Knee Walkers',
    description: 'Perfect for temporary mobility assistance during recovery from foot or ankle injuries. A comfortable alternative to crutches that allows you to remain active and mobile while keeping weight off your recovering injury.',
    icon: FootprintsIcon,
    color: 'text-brand-red',
    bgColor: 'bg-red-50',
    features: ['Tool-free adjustment', 'Dual braking system', 'Comfortable knee pad', 'Foldable for transport'],
    image: 'https://wheelaroundrentals.com/wp-content/uploads/2022/10/Image-1-280x242.png'
  }];
  return <main className="flex-grow w-full bg-white" data-id="element-1440">
      <PageHeader title="Our Services" breadcrumb="Services" onNavigateHome={() => onNavigate('home')} data-id="element-1441" />

      <section className="py-20 overflow-hidden" data-id="element-1442">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1443">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-1444">
            <p className="text-xl text-brand-charcoal/80 leading-relaxed" data-id="element-1445">
              We offer a diverse range of models to meet your specific mobility
              needs. Whether you're recovering from an injury or exploring the
              city, our team is always available to assist you in selecting the
              right equipment.
            </p>
          </div>

          <div className="space-y-24" data-id="element-1446">
            {services.map((service, index) => <div key={service.id} className={`flex flex-col ${index % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center`} data-id="element-1447">
                {/* Image Side */}
                <motion.div initial={{
              opacity: 0,
              x: index % 2 !== 0 ? 30 : -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="w-full lg:w-1/2" data-id="element-1448">
                  <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-video lg:aspect-square max-h-[400px] bg-gray-50 border border-gray-100" data-id="element-1449">
                    <img src={service.image} alt={service.title} className="w-full h-full object-contain mix-blend-multiply p-8" data-id="element-1450" />
                    <div className={`absolute bottom-6 left-6 w-16 h-16 rounded-full ${service.bgColor} ${service.color} flex items-center justify-center shadow-lg`} data-id="element-1451">
                      <service.icon className="w-8 h-8" data-id="element-1452" />
                    </div>
                  </div>
                </motion.div>

                {/* Content Side */}
                <motion.div initial={{
              opacity: 0,
              x: index % 2 !== 0 ? -30 : 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="w-full lg:w-1/2" data-id="element-1453">
                  <h2 className="text-3xl font-bold text-brand-navy mb-6" data-id="element-1454">
                    {service.title}
                  </h2>
                  <p className="text-lg text-brand-charcoal/70 mb-8 leading-relaxed" data-id="element-1455">
                    {service.description}
                  </p>

                  <h3 className="text-xl font-semibold text-brand-navy mb-4" data-id="element-1456">
                    Key Features:
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-1457">
                    {service.features.map((feature, fIndex) => <li key={fIndex} className="flex items-center gap-3" data-id="element-1458">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-red/10 flex items-center justify-center" data-id="element-1459">
                          <CheckIcon className="w-4 h-4 text-brand-red" data-id="element-1460" />
                        </div>
                        <span className="text-brand-charcoal/80 font-medium" data-id="element-1461">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <button onClick={() => onNavigate('reserve')} className="inline-flex items-center justify-center px-8 py-3 bg-brand-navy text-white rounded-full font-bold hover:bg-brand-blue transition-colors shadow-md" data-id="element-1462">
                    Reserve {service.title}
                  </button>
                </motion.div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-brand-gray border-t border-gray-200" data-id="element-1463">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1464">
          <h2 className="text-3xl font-bold text-brand-navy mb-6" data-id="element-1465">
            Need help choosing?
          </h2>
          <p className="text-lg text-brand-charcoal/70 mb-8" data-id="element-1466">
            Our friendly representatives will assist you in selecting the
            equipment that best fits your needs and provide accurate information
            on what we have in stock.
          </p>
          <button onClick={() => onNavigate('reserve')} className="px-8 py-4 bg-brand-red text-white rounded-full font-bold text-lg hover:bg-red-700 transition-all shadow-lg" data-id="element-1467">
            View our rates and reserve today
          </button>
        </div>
      </section>
    </main>;
}